<template>
  <div class="booking-delegate"
       :class="classes">
    <div class="booking-delegate__summary">
      <div class="booking-delegate__name">
        {{ delegateName }}
      </div>

      <button
        class="booking-delegate__edit"
        :aria-label="editing ? 'Edit delegate details' : 'Add delegate details'"
        @click="toggleEditing">
        <template v-if="editing">
          Close details
        </template>
        <template v-else>
          Add details
        </template>
      </button>

      <div v-if="enableDeferredDetails"
           class="booking-delegate__skip">
        <b-checkbox
          v-model="mutable.addDetailsLater"
          :aria-label="mutable.addDetailsLater ? 'Add details now' : 'Add details later'">
          Add details later
        </b-checkbox>
      </div>
    </div>

    <div v-if="editing"
         class="booking-delegate__form">
      <div v-if="loading"
           class="booking-delegate__loading">
        <loading-spinner></loading-spinner>
      </div>

      <b-form-radio-group v-model="mode"
                          v-slot="{ ariaDescribedby }"
                          :label="undefined"
                          class="booking-delegate__mode"
                          @change="resetMutable(false)">
        <b-form-radio :aria-describedby="ariaDescribedby"
                      name="existing-delegate"
                      value="WEBACCOUNT">
          Select an existing user to add as a delegate
        </b-form-radio>
        <b-form-radio :aria-describedby="ariaDescribedby"
                      name="new-delegate"
                      value="MANUAL">
          Add a new user as a delegate
        </b-form-radio>
      </b-form-radio-group>

      <div v-if="selectWebAccount"
           class="booking-delegate__web-account">
        <label>Select a user at an organisation to add as a delegate:</label>

        <b-form-group>
          <VSelect :options="orgs"
                   placeholder="Select an organisation"
                   :get-option-key="(option) => option.orgId"
                   :get-option-label="(option) => option.orgName"
                   @option:selected="onOrgSelected"></VSelect>
        </b-form-group>

        <b-form-group>
          <VSelect :options="webAccounts"
                   placeholder="Select an existing user"
                   :get-option-key="(option) => option.webAccountId"
                   :get-option-label="formattedUserLabel"
                   @option:selected="onWebAccountSelected"></VSelect>
        </b-form-group>

        <label>Selected user details:</label>

        <b-form-group>
          <b-input v-model="fullDelegateName"
                   placeholder="Delegate name"
                   type="text"
                   :readonly="true"></b-input>
        </b-form-group>

        <b-form-group>
          <b-input v-model="mutable.emailAddress"
                   placeholder="Email address"
                   type="text"
                   :readonly="true"></b-input>
        </b-form-group>
      </div>
      <div v-else
           class="booking-delegate__manual">
        <b-form-group>
          <b-select v-model="mutable.title"
                    :options="titleOptions"></b-select>
        </b-form-group>

        <b-form-group>
          <b-input v-model="mutable.firstName"
                   :placeholder="fieldPlaceholder.firstName || 'First name'"
                   :state="getState('firstName')"></b-input>

          <div v-if="getState('firstName') === false"
               class="validation-error">
            Please enter the delegate's first name
          </div>
        </b-form-group>

        <b-form-group>
          <b-input v-model="mutable.lastName"
                   :placeholder="fieldPlaceholder.lastName || 'Last name'"
                   :state="getState('lastName')"></b-input>

          <div v-if="getState('lastName') === false"
               class="validation-error">
            Please enter the delegate's last name
          </div>
        </b-form-group>

        <b-form-group>
          <VSelect v-model="mutable.jobTypeId"
                   :reduce="x => x.value"
                   :placeholder="fieldPlaceholder.jobType || 'Job type'"
                   :options="jobTypeOptions">
          </VSelect>
        </b-form-group>

        <b-form-group>
          <b-input v-model="mutable.emailAddress"
                   :placeholder="fieldPlaceholder.email || 'Email address'"
                   :state="getState('emailAddress') || validEmail"></b-input>

          <div v-if="getState('emailAddress') === false"
               class="validation-error">
            Please enter the delegate's email address
          </div>

          <div v-else-if="validEmail === false"
               class="validation-error">
            Please enter a valid email address
          </div>
        </b-form-group>
      </div>

      <b-form-group>
        <b-form-textarea id="textarea"
                         v-model="mutable.notes"
                         :placeholder="fieldPlaceholder.notes || 'Notes'"
                         rows="2"
                         max-rows="6">
        </b-form-textarea>
      </b-form-group>

      <b-form-group v-if="renderDietaryRequirements">
        <VSelect v-model="mutable.dietaryRequirementIds"
                 :reduce="x => x.value"
                 placeholder="Dietary requirements"
                 multiple
                 :options="dietaryRequirementOptions"></VSelect>
      </b-form-group>

      <b-form-group v-if="renderDisabilityOptions">
        <VSelect v-model="mutable.disabilityIds"
                 :reduce="x => x.value"
                 placeholder="Special requirements"
                 multiple
                 :options="disabilityOptions"></VSelect>
      </b-form-group>

      <div v-if="delegateTypesCount > 0">
        <b-form-group v-if="delegateTypesCount > 1"
           :class="{'field--invalid': showEventDelegateTypeVal}">
          <VSelect v-model="mutable.eventDelegateTypeId"
                   :reduce="x => x.value"
                   placeholder="Delegate Type"
                   :options="delegateTypeOptions"
                   :clearable="false"></VSelect>
          <div v-if="showEventDelegateTypeVal"
               class="validation-error">
            Please select a delegate type
          </div>
        </b-form-group>

        <MetaData v-model="mutable.metaData"></MetaData>
      </div>

      <div class="booking-delegate__actions"
           @click="onAccept">
        <button class="btn btn-accent-8 btn-sm btn-rounded"
                aria-label="Confirm delegate information">
          Accept
        </button>

        <button v-if="allowRemoval"
                class="booking-delegate__remove"
                @click="onRemove">
          Remove this delegate
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import VSelect from "vue-select";
import LoadingSpinner from "~/Components/LoadingSpinner.vue";
import { OrganisationResource } from "spiritweb.packages.web-account";
import MetaData from "~/Components/MetaData/MetaData.vue";

export default {
  name: "EventBookingDelegate",
  inject: {
    delegateBookingViewModel: { default: {} }
  },
  components: {
    VSelect,
    LoadingSpinner,
    MetaData
  },
  props: {
    value: {
      type: Object,
      default: () => ( {} )
    },
    index: {
      type: Number,
      default: 1
    },
    valid: {
      type: Boolean,
      default: true
    },
    showValidity: {
      type: Boolean,
      default: false
    },
    allowRemoval: {
      type: Boolean,
      default: false
    },
    showDisabilityOptions: {
      type: Boolean,
      default: false
    },
    disabilities: {
      type: Array,
      default: () => []
    },
    showDietaryRequirements: {
      type: Boolean,
      default: false
    },
    dietaryRequirements: {
      type: Array,
      default: () => []
    },
    jobTypes: {
      type: Array,
      default: () => []
    },
    edit: {
      type: Boolean,
      default: false
    },
    orgs: {
      type: Array,
      default: () => []
    },
    delegateTypes: {
      type: Array,
      default: null
    },
    placeholder: {
      type: Object,
      required: false,
      default: undefined,
      validator: val => {
        const schema = ["firstName", "lastName", "jobType", "email", "notes"];
        return Object.keys( val ).every( key => {
          const isValid = schema.includes( key );

          if ( isValid === false && process.env.NODE_ENV !== "production" ) {
            console.warn(
              `The following property "${key}" does not represent a valid key for placeholders.`
            );
            console.info( "Valid properties: ", schema );
          }

          return isValid;
        } );
      }
    },
    enableDeferredDetails: {
      type: Boolean,
      default: false
    }
  },
  data: () => ( {
    editing: false,
    mode: "WEBACCOUNT",
    webAccounts: [],
    loading: false,
    showEventDelegateTypeVal: false,
    mutable: {
      id: null,
      title: null,
      firstName: null,
      lastName: null,
      jobTitle: null,
      jobTypeId: null,
      emailAddress: null,
      notes: null,
      dietaryRequirementIds: [],
      disabilityIds: [],
      eventDelegateTypeId: null,
      orgId: null,
      personId: null,
      addDetailsLater: false,
      metaData: null
    }
  } ),
  computed: {
    classes () {
      const result = [];

      if ( !this.valid && this.showValidity ) {
        result.push( "booking-delegate--invalid" );
      }

      return result;
    },
    delegateTypeId () {
      return this.mutable.eventDelegateTypeId;
    },
    titleOptions () {
      return [
        {
          text: "- Select a title -",
          value: null
        },
        "Dr",
        "Miss",
        "Mr",
        "Mrs",
        "Ms"
      ];
    },
    selectWebAccount () {
      return this.mode === "WEBACCOUNT";
    },
    renderDisabilityOptions () {
      return this.hasDisabilityOptions && this.showDisabilityOptions;
    },
    renderDietaryRequirements () {
      return this.hasDietaryRequirements && this.showDietaryRequirements;
    },
    hasDisabilityOptions () {
      return this.disabilityOptions.length > 0;
    },
    hasDietaryRequirements () {
      return this.dietaryRequirementOptions.length > 0;
    },
    disabilityOptions () {
      return this.disabilities.map( x => ( {
        value: x.eventDisabilityId,
        label: x.disability
      } ) );
    },
    dietaryRequirementOptions () {
      return this.dietaryRequirements.map( x => ( {
        value: x.eventDietaryRequirementId,
        label: x.dietaryRequirement
      } ) );
    },
    delegateTypesCount () {
      let count = 0;
      if ( this.delegateTypes && this.delegateTypes !== undefined ) {
        count = this.delegateTypes.length;
      }
      return count;
    },
    delegateTypeOptions () {
      return this.delegateTypes.map( x => ( {
        value: x.eventDelegateTypeId,
        label: x.delegateType
      } ) );
    },
    jobTypeOptions () {
      return this.jobTypes.map( x => ( {
        value: x.jobTypeId,
        label: x.jobType
      } ) );
    },
    delegateName () {
      let result = `Delegate ${this.index}`;

      const { firstName = "", lastName = "" } = this.mutable;
      if ( firstName || lastName ) {
        result = `${firstName ?? ""} ${lastName ?? ""}`.trim();
      }

      return result;
    },
    fullDelegateName () {
      const { firstName, lastName, title } = this.mutable;
      return `${title ?? ""} ${firstName ?? ""} ${lastName ?? ""}`.trim();
    },
    validEmail () {
      let result = null;

      if ( this.showValidity && !this.valid ) {
        const isValid = this.emailIsValid( this.mutable.emailAddress );

        if ( !isValid ) {
          result = false;
        }
      }

      return result;
    },
    fieldPlaceholder () {
      let mutablePlaceholders = { ...this.placeholder };

      /// injected placeholders takes precedence
      if ( this.delegateBookingViewModel ) {
        const {
          FirstNameInputDetails,
          LastNameInputDetails,
          JobTypeInputDetails,
          EmailInputDetails,
          NotesInputDetails
        } = this.delegateBookingViewModel;

        if ( FirstNameInputDetails ) {
          const { Placeholder } = FirstNameInputDetails;
          mutablePlaceholders = { ...mutablePlaceholders, firstName: Placeholder };
        }
        if ( LastNameInputDetails ) {
          const { Placeholder } = LastNameInputDetails;
          mutablePlaceholders = { ...mutablePlaceholders, lastName: Placeholder };
        }
        if ( JobTypeInputDetails ) {
          const { Placeholder } = JobTypeInputDetails;
          mutablePlaceholders = { ...mutablePlaceholders, jobType: Placeholder };
        }
        if ( EmailInputDetails ) {
          const { Placeholder } = EmailInputDetails;
          mutablePlaceholders = { ...mutablePlaceholders, email: Placeholder };
        }
        if ( NotesInputDetails ) {
          const { Placeholder } = NotesInputDetails;
          mutablePlaceholders = { ...mutablePlaceholders, notes: Placeholder };
        }
      }

      return mutablePlaceholders;
    },
    isMutableTouched () {
      let result = false;

      if ( this.mutable ) {
        const {
          // eslint-disable-next-line
          firstName, lastName, jobTitle, emailAddress, disabilityIds, dietaryRequirementIds,
          notes, title, jobTypeId, orgId, personId, eventDelegateTypeId, metaData
        } = this.mutable;

        // eslint-disable-next-line
        const hasUserProvidedAnyText = [ firstName, lastName, jobTitle, emailAddress, notes,
          title, jobTypeId, orgId, personId, eventDelegateTypeId, metaData]
          .some( ( str ) => !!str );
        let hasUserProvidedAnyOptions = false;

        if ( Array.isArray( disabilityIds ) ) {
          hasUserProvidedAnyOptions = disabilityIds.length > 0;
        }
        if ( !hasUserProvidedAnyOptions && Array.isArray( dietaryRequirementIds ) ) {
          hasUserProvidedAnyOptions = dietaryRequirementIds.length > 0;
        }

        result = hasUserProvidedAnyText || hasUserProvidedAnyOptions;
      }

      return result;
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler ( val ) {
        Object.keys( this.mutable ).forEach( x => {
          this.mutable[x] = val[x];
        } );
      }
    },
    mutable: {
      deep: true,
      handler ( val ) {
        this.$nextTick( () => {
          if ( this.isMutableTouched && val && val.addDetailsLater ) {
            this.resetMutable( true );
            this.editing = false;
          }
        } );

        this.$emit( "input", val );
      }
    },
    editing: {
      handler ( val ) {
        if ( val ) {
          this.$set( this.mutable, "addDetailsLater", false );
        }
      }
    },
    isMutableTouched: {
      handler ( val ) {
        if ( val ) {
          this.$set( this.mutable, "addDetailsLater", false );
        }
      }
    },
    delegateTypeId: {
      deep: true,
      handler ( val ) {
        this.$nextTick( () => {
          if ( val > 0 && this.delegateTypes && this.delegateTypes !== undefined
            && this.delegateTypes.length > 0 ) {
            const delegateTypeData = this.delegateTypes.find(
              ( x ) => x.eventDelegateTypeId === val );

            if ( delegateTypeData && delegateTypeData !== undefined ) {
              this.mutable.metaData = delegateTypeData.metadatas;
            }
          } else {
            this.mutable.metaData = null;
          }
        } );
      }
    }
  },
  mounted () {
    if ( this.edit && this.mutable?.personId ) {
      // We are in edit mode and we have a person ID, so this delegate must have been already
      // selected from a list of web accounts. In that case we need to switch to WEBACCOUNT mode
      this.mode = "WEBACCOUNT";
    }

    this.resetDelegateTypeId();
  },
  methods: {
    resetMutable ( addDetailsLater = false ) {
      const blankDelegate = {
        title: null,
        firstName: null,
        lastName: null,
        jobTitle: null,
        jobTypeId: null,
        emailAddress: null,
        notes: null,
        dietaryRequirementIds: [],
        disabilityIds: [],
        eventDelegateTypeId: null,
        metaData: null,
        orgId: null,
        personId: null,
        addDetailsLater
      };

      if ( this.edit ) {
        this.mutable = {
          ...this.mutable,
          ...blankDelegate
        };
      } else {
        this.mutable = {
          ...blankDelegate,
          id: null
        };
      }

      if ( !addDetailsLater ) {
        this.resetDelegateTypeId();
      }
    },
    resetDelegateTypeId () {
      if ( this.delegateTypes && this.delegateTypes !== undefined
        && this.delegateTypes.length === 1 ) {
        // Default the delegate type to the first one
        this.mutable.eventDelegateTypeId = this.delegateTypes[0].eventDelegateTypeId;
      } else {
        // Either there are no delegate types or more than one, so set to null
        this.mutable.eventDelegateTypeId = null;
      }
    },
    toggleEditing () {
      this.editing = !this.editing;
    },
    onAccept () {
      this.showEventDelegateTypeVal = this.delegateTypesCount > 0
      && !this.mutable.eventDelegateTypeId;

      if ( !this.showEventDelegateTypeVal ) {
        this.editing = false;
        this.$emit( "input", this.mutable );
      }
    },
    onRemove () {
      this.$emit( "remove" );
    },
    getState ( field ) {
      let result = null;

      if ( this.showValidity && !this.valid ) {
        if ( Array.isArray( this.mutable[field] ) && this.mutable[field].length === 0 ) {
          result = false;
        } else if ( !this.mutable[field] ) {
          result = false;
        }
      }

      return result;
    },
    emailIsValid ( email ) {
      return /\S+@\S+\.\S+/.test( email );
    },

    /**
     * Load a list of web accounts for an organisation
     * @param {String} orgId Orgaisation to lod web accounts for
     */
    getWebAccounts ( orgId ) {
      this.loading = true;

      OrganisationResource.getUsersByOrganisation(
        orgId,
        null,
        null,
        null,
        { namespace: this.$options.name } )
        .then( response => {
          this.webAccounts = response?.data || [];
        } )
        .catch( err => {
          console.error( err );
        } )
        .finally( () => {
          this.loading = false;
        } );
    },

    /**
     * Build a user name to use n the web accounts drop down
     * @param {object} webAccount The web account to get the user name label from
     */
    formattedUserLabel ( webAccount ) {
      let result;

      if ( typeof this.getUserLabel === "function" ) {
        result = this.getUserLabel( webAccount );
      } else {
        result = `${webAccount?.personFirstName || ""} ${webAccount?.personLastName || ""}`;
      }

      return result;
    },

    /**
     * A web account has been selected
     * @param {object} webAccount The selected web account
     */
    onWebAccountSelected ( webAccount ) {
      this.mutable.title = webAccount.personTitle;
      this.mutable.firstName = webAccount.personFirstName;
      this.mutable.lastName = webAccount.personLastName;
      this.mutable.jobTitle = webAccount.personJobTitle;
      this.mutable.emailAddress = webAccount.emailAddress;
      this.mutable.personId = webAccount.personId;
      this.mutable.orgId = webAccount.orgId;
    },

    /**
     * An organisation has been selected, so load a new set of web accounts
     * @param {object} org The selected organisation
     */
    onOrgSelected ( org ) {
      if ( org ) {
        this.getWebAccounts( org.orgId );
      }
    }
  }
};
</script>

<style lang="less" scoped>
.booking-delegate {
  border: 2px solid #80bdbf;
  border-radius: 5px;
  margin-bottom: 10px;

  &__mode {
    display: flex;
    flex-flow: column;
    gap: 4px;
    margin: 10px 0;

    /deep/ .custom-control-input:checked {
      ~ .custom-control-label::before {
        color: #fff;
        border-color: #4fc07d;
        background-color: #4fc07d;
      }
    }

    /deep/ .custom-control-input:not(:disabled) {
      ~ .custom-control-label::before,
      ~ .custom-control-label::after {
        cursor: pointer;
      }

      &:active {
        ~ .custom-control-label::before {
          color: #fff;
          background-color: lighten(#4fc07d, 15%);
          border-color: lighten(#4fc07d, 15%);
        }
      }
    }
  }

  &__summary {
    display: flex;
    justify-content: space-between;
    align-items: stretch;

    @media (max-width: 991px) {
      flex-direction: column;
    }
  }

  &__name,
  &__edit,
  &__skip {
    padding: 10px 15px;
    flex-basis: 33%;
    flex-grow: 1;
    border: 0;
  }

  &__name {
    font-weight: 700;
  }

  &__edit {
    background: #e3f3ea;
    border-left: 2px solid #80bdbf;
    border-right: 2px solid #80bdbf;
    transition: background-color 0.2s;
    font-size: 14px;

    &:hover {
      background: darken(#e3f3ea, 10%);
    }

    &:last-child {
      border-right: 0;
      border-radius: 0 5px 0 0;
    }

    @media (max-width: 991px) {
      border-left: 0;
      border-right: 0;
      border-top: 2px solid #80bdbf;
      border-bottom: 2px solid #80bdbf;
      text-align: left;

      &:last-child {
        border-bottom: 0;
        border-radius: 0 0 5px 5px;
      }
    }
  }

  &__skip {
    background: #f1f9f4;
    border-radius: 0 5px 0 0;
    display: flex;
    justify-content: stretch;
    font-size: 14px;

    > div {
      display: flex;
      flex-grow: 1;
      align-items: center;
    }

    /deep/ label {
      flex-grow: 1;
    }

    @media (max-width: 991px) {
      border-radius: 0 0 5px 5px;
    }
  }

  &__form {
    position: relative;
    padding: 10px;
  }

  &__loading {
    z-index: 9999;
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(100, 100, 100, 0.2);
  }

  &__actions {
    display: flex;
    justify-content: space-between;
  }

  &__remove {
    color: #0c499c;
    padding: 0;
    border: 0;
    background: none;
    font-size: 14px;

    &:hover {
      color: darken(#0c499c, 15%);
      text-decoration: underline;
    }
  }

  &--invalid {
    border-color: red;
  }
}

/deep/ .vs__dropdown-toggle {
  padding: 1px 5px 5px 5px;
}

/deep/ .vs__search {
  padding-top: 4px;
  padding-bottom: 4px;
}

/deep/ .v-select {
  font-size: 14px;

  ::placeholder {
    color: #7f7f7f;
    opacity: 1;
  }
}

.field--invalid {
  /deep/ .vs__dropdown-toggle {
    border-color: red !important;
  }
}

</style>
